import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import useSeo from "@/hooks/useSeo";
import { Truck, Sparkles, Car, PenTool, ClipboardCheck, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    icon: Truck,
    title: "Fahrzeuganlieferung",
    lead: "Deutschlandweit direkt vor Ihre Haustür.",
    desc: "Wir liefern Ihr neues Fahrzeug persönlich, sicher und pünktlich – inklusive Einweisung. Auf Wunsch auch europaweit.",
    image: "https://images.unsplash.com/photo-1600661653561-629509216228?q=80&w=1600",
  },
  {
    icon: Sparkles,
    title: "Professionelle Fahrzeugaufbereitung",
    lead: "Neuwagen-Feeling für jedes Auto.",
    desc: "Innen- und Außenreinigung, Politur, Lackversiegelung, Lederpflege und Innenraum-Detailing – von unseren Aufbereitungs­spezialisten in Köln.",
    image: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=1600",
  },
  {
    icon: Car,
    title: "Abholservice",
    lead: "Wir holen Ihr Fahrzeug bei Ihnen ab.",
    desc: "Beim Ankauf, bei der Inzahlungnahme oder für Service-Arbeiten: unser Kurier bringt Ihr Auto in die MotorWorld Köln – Sie müssen keinen Meter fahren.",
    image: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?q=80&w=1600",
  },
  {
    icon: PenTool,
    title: "Fahrzeugbeschriftung & Folierung",
    lead: "Individuelles Design in Premium-Qualität.",
    desc: "Von dezenten Akzenten bis zur Vollfolierung. Werbebeschriftung, Farbwechsel, Chrome-Delete, Steinschlagschutz – ausgeführt durch zertifizierte Partner.",
    image: "https://images.unsplash.com/photo-1520187037150-1e6efee45cf7?q=80&w=1600",
  },
  {
    icon: ClipboardCheck,
    title: "Zulassungsservice",
    lead: "Papierkram? Übernehmen wir.",
    desc: "Kennzeichen, Zulassung, Ummeldung, Kurzzeit- und Ausfuhrkennzeichen. Sie holen Ihr Auto zulassungsfertig ab – wir kümmern uns um den Rest.",
    image: "https://images.unsplash.com/photo-1523983388277-336a66bf9bcd?q=80&w=1600",
  },
];

const HERO_IMG = "https://images.unsplash.com/photo-1611288891005-e91c5b5b83c8?q=80&w=2200";

export default function Service() {
  useEffect(() => window.scrollTo(0, 0), []);
  useSeo({
    title: "Service · Fahrzeuganlieferung, Aufbereitung, Zulassung | DNA Automotive Köln",
    description: "Rundum-Service in der MotorWorld Köln: Fahrzeuganlieferung, professionelle Aufbereitung, Abholservice, Folierung und Zulassungsservice. Alles aus einer Hand.",
    path: "/service",
    breadcrumbs: [{ name: "Service", url: "/service" }],
  });
  const scrollContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-obsidian min-h-screen" data-testid="service-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-32 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/40" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">Service · MotorWorld Köln</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
            Rundum-Service.<br /><span className="text-brand-bright">Ohne Aufwand.</span>
          </h1>
          <p className="text-zinc-300 max-w-2xl text-lg mt-8">
            Wir übernehmen alles rund um Ihr Fahrzeug – von der Anlieferung bis zur Zulassung. Diskret, präzise, auf Premium-Niveau.
          </p>
        </div>
      </section>

      {/* Service items — alternating large image blocks */}
      <section className="px-6 md:px-10 pb-24" data-testid="service-list">
        <div className="max-w-[1600px] mx-auto space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}
                data-testid={`service-item-${i}`}
              >
                <div className="lg:col-span-7 [direction:ltr]">
                  <div className="aspect-[16/10] overflow-hidden border border-white/10 group">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="lg:col-span-5 [direction:ltr]">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="font-heading font-black text-brand-bright/30 text-6xl leading-none">{String(i + 1).padStart(2, "0")}</span>
                    <Icon className="w-7 h-7 text-brand-bright" />
                  </div>
                  <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] mb-4">
                    {s.title}
                  </h2>
                  <p className="text-brand-bright text-sm uppercase tracking-widest mb-5">{s.lead}</p>
                  <p className="text-zinc-400 leading-relaxed text-base md:text-lg">{s.desc}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* CTA band */}
      <section className="px-6 md:px-10 py-24 border-t border-white/10">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">Persönliche Anfrage</p>
            <h3 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] max-w-2xl">
              Ihr Service auf Maß.
            </h3>
          </div>
          <button
            onClick={scrollContact}
            className="group inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover transition-colors"
            data-testid="service-cta"
          >
            Service anfragen
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <ContactSection prefillVehicle="Service-Anfrage" />
      <Footer />
      <WhatsAppButton prefill="Hallo DNA Automotive, ich habe eine Service-Anfrage." />
    </div>
  );
}
